import { useState, useEffect } from 'react';
import { api } from '../lib/api';

// ── What you've already asked us ──
//
// Sits under each of the three request forms. Before it existed the form said
// "we'll be in touch" and then nothing on screen ever changed again, so a
// customer with no reply had no way to tell whether we'd received anything —
// and the honest response to that is to send it again. One product request
// arrived twice in thirteen minutes that way.
//
// Scoped to the type of the form it appears under: the point is recognising
// "I already asked for this", not browsing an archive.

const STATUS_COLORS = {
  new: { bg: '#eef2ff', fg: '#3340a0' },
  in_progress: { bg: '#fff3e0', fg: '#b26500' },
  done: { bg: '#e6f4ea', fg: '#2e7d32' },
};

const TYPE_NOUN = {
  service: 'service requests',
  product: 'product requests',
  feedback: 'messages',
};

function whenText(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  if (isNaN(then)) return '';
  const days = Math.floor((Date.now() - then.getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 8) return `${days} days ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function MyRequests({ type }) {
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getMyRequests()
      .then(data => {
        if (cancelled) return;
        setRequests((data?.requests || []).filter(r => r.type === type));
      })
      // A failure here must never get in the way of submitting the form —
      // this is reassurance, not function.
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [type]);

  if (!loaded || !requests.length) return null;

  return (
    <div style={s.wrap}>
      <h3 style={s.heading}>Your recent {TYPE_NOUN[type] || 'requests'}</h3>
      <p style={s.note}>
        We have these on file. There's no need to send them again.
      </p>
      <ul style={s.list}>
        {requests.map(r => {
          const colors = STATUS_COLORS[r.status] || STATUS_COLORS.new;
          return (
            <li key={r.requestId} style={s.row}>
              <div style={s.rowMain}>
                <div style={s.summary}>{r.summary || 'Your request'}</div>
                <div style={s.meta}>
                  Sent {whenText(r.createdAt)}
                  {r.submittedByEmail ? ` by ${r.submittedByEmail}` : ''}
                </div>
              </div>
              <span style={{ ...s.chip, background: colors.bg, color: colors.fg }}>
                {r.statusLabel}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const s = {
  wrap: { maxWidth: 720, margin: '1.5rem auto 0', padding: '1.1rem 1.25rem', background: '#fff', border: '1px solid #eee', borderRadius: 12 },
  heading: { fontSize: '0.95rem', fontWeight: 700, color: '#222', margin: '0 0 0.2rem' },
  note: { fontSize: '0.82rem', color: '#888', margin: '0 0 0.75rem' },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  row: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', padding: '0.6rem 0', borderTop: '1px solid #f3f3f3', flexWrap: 'wrap' },
  rowMain: { flex: 1, minWidth: 200 },
  summary: { fontSize: '0.9rem', color: '#222', fontWeight: 500 },
  meta: { fontSize: '0.78rem', color: '#999', marginTop: '0.15rem' },
  chip: { borderRadius: 6, padding: '0.15rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' },
};
